// src/pages/ManagerProfile.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff, FiUsers, FiUserCheck, FiUser, FiClipboard, FiSettings, FiUserPlus, FiArrowRight, FiFileText, FiTruck } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useClient } from '../contexts/ClientContext';
import PhoneInput, { validateIsraeliPhone } from '../components/PhoneInput';
import EmailInput from '../components/EmailInput';
import { getManagerById, updateManager, changeManagerPassword } from '../api/managersApi';
import { ALL_PERMISSIONS, applyDependenciesOnToggle, hasFullPermissions, PERMISSION_TYPES, PERMISSION_DESCRIPTIONS, PERMISSION_TYPE_LABELS, PERMISSION_DEPENDENCIES } from '../config/permissionsConfig';
import { Tooltip } from '../components/ToolTip';
import { useQueryClient } from '@tanstack/react-query';
import { managerKeys } from '../lib/queryKeys';
import { useLang } from '../contexts/LanguageContext';

// ---- types ----
interface Manager {
  fullName: string;
  email: string;
  phone?: string;
  permissions?: string[];
}

type PermGroup = {
  key: string;
  title: string;
  icon: React.ReactNode;
  permissions: string[];
};

// ---- helpers ----
const getCompanyFromToken = (): string | undefined => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    return payload?.companyNumber as string | undefined;
  } catch {
    return;
  }
};

export const permissionLabels: Record<string, string> = {
  // Leads
  view_leads: 'צפייה בלידים',
  create_leads: 'יצירת לידים',
  edit_leads: 'עריכת לידים',
  assign_leads: 'הקצאת לידים',
  manage_leads: 'ניהול לידים',

  // Clients
  edit_clients: 'עריכת לקוחות',
  add_clients: 'הוספת לקוחות',
  renew_clients: 'חידוש מנוי',
  change_client_password: 'שינוי סיסמת לקוח',
  view_client_details: 'צפייה בפרטי לקוח',
  view_client_agent: 'צפייה בסוכן הלקוח',
  view_client_payment: 'צפייה בתשלומי לקוח',
  edit_client_payment: 'עריכת תשלומי לקוח',
  view_payments: 'צפייה בתשלומים',
  manage_clients: 'ניהול לקוחות',
  transfer_client_to_agent: 'העברת לקוח לסוכן',
  delete_clients: 'מחיקת לקוחות',

  // Tasks
  view_tasks: 'צפייה במשימות',
  create_tasks: 'יצירת משימות',
  edit_tasks: 'עריכת משימות',
  delete_tasks: 'מחיקת משימות',
  complete_tasks: 'סימון משימות כהושלמו',
  manage_task_kinds: 'ניהול סוגי משימות',

  // Managers
  view_manager: 'צפייה במשתמשים',
  create_manager: 'יצירת משתמשים',
  edit_manager: 'עריכת משתמש',
  delete_manager: 'מחיקת משתמש',
  change_manager_password: 'שינוי סיסמת משתמש',

  // Agents
  create_agent: 'יצירת סוכנים',
  view_agent: 'צפייה בסוכנים',
  edit_agent: 'עריכת סוכנים',
  change_agent_password: 'שינוי סיסמת סוכן',

  // Potential Clients
  view_potential_clients: 'צפייה בלקוחות פוטנציאליים',
  edit_potential_clients: 'עריכת לקוחות פוטנציאליים',
  delete_potential_clients: 'מחיקת לקוחות פוטנציאליים',
  convert_potential_clients: 'המרת ליד ללקוח',

  // System
  view_slots: 'צפייה בתורים',
  create_slots: 'יצירת תורים',
  edit_slots: 'עריכת תורים',
  overview: 'נתונים כלליים',
  manage_orders: 'ניהול הזמנות',
  manage_api_keys: 'ניהול מפתחות API',
  view_sessions: 'צפייה בפעילויות',
  view_failed_logins: 'צפייה בניסיונות כניסה כושלים',
  unlock_accounts: 'שחרור חשבונות נעולים',
  // Proposals (Price Quotes)
  view_proposals: 'צפייה בהצעות מחיר',
  create_proposals: 'יצירת הצעות מחיר',
  edit_proposals: 'עריכת הצעות מחיר',
  // Vendors
  view_vendors: 'צפייה בספקים',
  edit_vendors: 'עריכת ספקים',
};

const permissionGroups: PermGroup[] = [
  {
    key: 'clients',
    icon: <FiUsers />,
    title: 'לקוחות',
    permissions: [
      'edit_clients',
      'add_clients', // הוספת לקוחות - controls visibility of "לקוח חדש" button for managers
      'change_client_password',
      'view_client_details',
      'view_client_payment',
      'edit_client_payment',
      'manage_clients',
      'transfer_client_to_agent',
      // 'delete_clients' - Hidden from managers, admin-only permission
    ],
  },
  {
    key: 'leads',
    icon: <FiUserPlus />,
    title: 'לידים',
    permissions: [
      'view_leads',
      'create_leads',
      'edit_leads',
      'assign_leads',
      'manage_leads',
    ],
  },
  {
    key: 'agents',
    icon: <FiUserCheck />,
    title: 'סוכנים',
    permissions: [
      'create_agent',
      'view_agent',
      'edit_agent',
      'change_agent_password',
      // 'delete_agent' - Hidden from managers, admin-only permission
    ],
  },
  {
    key: 'managers',
    icon: <FiUser />,
    title: 'משתמשים / משימות',
    permissions: [
      'view_tasks',
      'edit_manager',
      // 'delete_manager' - Hidden from managers, admin-only permission
      'view_manager',
      'create_manager',
      'change_manager_password',
      'manage_task_kinds',
    ],
  },
  {
    key: 'potential',
    icon: <FiClipboard />,
    title: 'לקוחות פוטנציאליים',
    permissions: [
      'view_potential_clients',
      'edit_potential_clients',
      // 'delete_potential_clients' - Hidden from managers, admin-only permission
      'convert_potential_clients',
    ],
  },
  {
    key: 'system',
    icon: <FiSettings />,
    title: 'מערכת כללית',
    permissions: [
      'view_slots',
      'create_slots',
      'edit_slots',
      'overview',
      'manage_orders',
      'manage_api_keys',
      'view_sessions',
      'view_failed_logins',
      'unlock_accounts',
    ],
  },
  {
    key: 'proposals',
    icon: <FiFileText />,
    title: 'הצעות מחיר',
    permissions: [
      'view_proposals',
      'create_proposals',
      'edit_proposals',
    ],
  },
  {
    key: 'vendors',
    icon: <FiTruck />,
    title: 'ספקים',
    permissions: ['view_vendors', 'edit_vendors'],
  },
];

// simple separator (no external deps)
const Separator: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`h-px w-full bg-gray-200 ${className || ''}`} />
);

// ---- component ----
const ManagerProfile: React.FC = () => {
  const { t } = useLang();
  const { id } = useParams<{ id: string }>();
  const { company: companyParam } = useParams<{ company: string }>();
  const company = useMemo(getCompanyFromToken, []);
  const navigate = useNavigate();
  const { client, refreshClient } = useClient();
  const queryClient = useQueryClient();
  
  // Check if current user is viewing their own profile and is manager/agent (read-only mode)
  const isViewingOwnProfile = client?._id === id;
  const isManagerOrAgent = client?.role === 'manager' || client?.role === 'agent';
  const isReadOnly = isViewingOwnProfile && isManagerOrAgent;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [manager, setManager] = useState<Manager | null>(null); // Kept for potential future use
  const [formData, setFormData] = useState<Manager | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  // Validation errors state
  const [validationErrors, setValidationErrors] = useState<{
    fullName?: string;
    email?: string;
    phone?: string;
  }>({});

  // Track which fields are focused (to show "✓ תקין" only while typing)
  const [focusedFields, setFocusedFields] = useState<{
    fullName?: boolean;
    phone?: boolean;
    email?: boolean;
  }>({});

  // password state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isSavingPassword, setIsSavingPassword] = useState<boolean>(false);

  // confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Manager type state (to track UI selection independent of permissions)
  const [managerTypeOverride, setManagerTypeOverride] = useState<'full-access' | 'regular' | 'free-choice' | null>(null);

  // collapsible sections
  const [openSections, setOpenSections] = useState<string[]>([]);

  const toggleSection = (key: string) => {
    setOpenSections((prev: string[]) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // fetch
  useEffect(() => {
    const run = async () => {
      if (!company || !id) return;
      try {
        const data = await getManagerById(company, id);
        setManager(data);
        setFormData(data);
        // Reset override when loading new manager
        setManagerTypeOverride(null);
      } catch (err: unknown) {
        console.error(err);
        toast.error(t('pages.managerProfile.loadError'));
      }
    };
    void run();
  }, [company, id]);

  // Validation functions (use t for messages)
  const validateField = (field: string, value: string): string => {
    switch (field) {
      case 'fullName':
        if (!value || value.trim().length < 2) {
          return t('pages.managerProfile.validationFullNameMin');
        }
        return '';
      case 'email':
        if (!value || value.trim().length === 0) {
          return t('pages.managerProfile.validationEmailRequired');
        }
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(value.trim())) {
          return t('pages.managerProfile.validationEmailInvalid');
        }
        return '';
      case 'phone':
        if (value && value.trim().length > 0) {
          const phoneValidation = validateIsraeliPhone(value);
          if (!phoneValidation.isValid) {
            return phoneValidation.error || t('pages.managerProfile.validationPhoneInvalid');
          }
        }
        return '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateAll = (): boolean => {
    if (!formData) return false;
    const errors: typeof validationErrors = {};
    errors.fullName = validateField('fullName', formData.fullName);
    errors.email = validateField('email', formData.email);
    errors.phone = validateField('phone', formData.phone || '');
    
    setValidationErrors(errors);
    return !Object.values(errors).some(e => e !== '');
  };

  const handleSave = async () => {
    if (!formData || !company || !id) return;
    
    // Clear previous errors
    setValidationErrors({});
    
    // Validate all fields
    if (!validateAll()) {
      toast.error(t('pages.managerProfile.fillAllRequired'));
      return;
    }
    
    setSaving(true);
    try {
      // Debug: Log what we're sending
      console.log('[ManagerProfile] Saving manager data:', {
        managerId: id,
        permissions: formData.permissions,
        permissionsList: Array.isArray(formData.permissions) ? formData.permissions.join(', ') : 'not array',
        permissionsCount: Array.isArray(formData.permissions) ? formData.permissions.length : 0,
        hasViewProposals: Array.isArray(formData.permissions) ? formData.permissions.includes('view_proposals') : false,
        hasCreateProposals: Array.isArray(formData.permissions) ? formData.permissions.includes('create_proposals') : false,
        hasEditProposals: Array.isArray(formData.permissions) ? formData.permissions.includes('edit_proposals') : false,
        fullFormData: formData
      });
      
      await updateManager(company, id, formData);
      setManager(formData);
      
      // Invalidate managers cache to refresh the list immediately
      if (company) {
        queryClient.invalidateQueries({ queryKey: managerKeys.all(company) });
      }
      
      await refreshClient(); // in case current user edits self permissions
      toast.success(t('pages.managerProfile.saveSuccess'));
    } catch (err: unknown) {
      console.error(err);
      const axiosError = err as any;
      const responseData = axiosError?.response?.data;
      const status = axiosError?.response?.status;
      
      // Handle specific error cases
      let errorMessage = t('pages.managerProfile.saveError');
      let emailError = false;
      
      if (status === 400) {
        // Check for email already in use
        const errorText = responseData?.error || responseData?.message || '';
        const lowerError = errorText.toLowerCase();
        
        // Check for various email duplicate error messages
        if (
          lowerError.includes('email') && (
            lowerError.includes('already') || 
            lowerError.includes('exists') || 
            lowerError.includes('בשימוש') || 
            lowerError.includes('קיים') ||
            lowerError.includes('רשום')
          ) ||
          errorText.includes('אימייל זה כבר רשום במערכת') ||
          errorText.includes('Email already exists')
        ) {
          errorMessage = t('pages.managerProfile.emailInUse');
          emailError = true;
          // Set email field error
          setValidationErrors({ email: errorMessage });
        } else if (responseData?.error) {
          errorMessage = responseData.error;
        } else if (responseData?.message) {
          errorMessage = responseData.message;
        }
      } else if (responseData?.error) {
        errorMessage = responseData.error;
        // Check if it's an email error even if status is not 400
        const lowerError = errorMessage.toLowerCase();
        if (lowerError.includes('email') && (lowerError.includes('already') || lowerError.includes('exists') || lowerError.includes('בשימוש') || lowerError.includes('קיים'))) {
          emailError = true;
          setValidationErrors({ email: errorMessage });
        }
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      } else if (axiosError?.message) {
        errorMessage = axiosError.message;
      }
      
      toast.error(errorMessage);
      
      // Set specific field errors if available (but not email, as we already set it above)
      if (responseData?.field && responseData.field !== 'email' && !emailError) {
        setValidationErrors({ [responseData.field]: errorMessage });
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!company || !id) return;
    if (newPassword.length < 6) {
      toast.error(t('pages.managerProfile.passwordMin'));
      return;
    }
    setIsSavingPassword(true);
    try {
      await changeManagerPassword(company, id, newPassword);
      toast.success(t('pages.managerProfile.passwordSuccess'));
      setIsPasswordModalOpen(false);
      setNewPassword('');
    } catch (err: unknown) {
      console.error(err);
      toast.error(t('pages.managerProfile.passwordError'));
    } finally {
      setIsSavingPassword(false);
    }
  };

  if (!formData) {
    return (
      <div className="p-6 text-right">
        <div className="max-w-2xl mx-auto bg-white shadow-lg rounded-2xl p-8">
          <div className="flex flex-col items-center justify-center py-16">
            {/* Brand text at the top */}
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 loading-brand">
                WJS 365ᵈ Together
              </h2>
            </div>
            
            {/* Animated text with typing effect */}
            <div className="flex items-center gap-2 text-gray-700 text-xl font-semibold mb-2">
              <span className="loading-text-word">{t('pages.managerProfile.loadingWord1')}</span>
              <span className="loading-text-word" style={{ animationDelay: '0.3s' }}>{t('pages.managerProfile.loadingWord2')}</span>
              <span className="loading-text-word" style={{ animationDelay: '0.6s' }}>{t('pages.managerProfile.loadingWord3')}</span>
            </div>
            
            {/* Animated dots */}
            <div className="flex gap-2 mb-6">
              <span className="w-3 h-3 bg-blue-500 rounded-full loading-dot" style={{ animationDelay: '0s' }}></span>
              <span className="w-3 h-3 bg-blue-500 rounded-full loading-dot" style={{ animationDelay: '0.2s' }}></span>
              <span className="w-3 h-3 bg-blue-500 rounded-full loading-dot" style={{ animationDelay: '0.4s' }}></span>
            </div>
            
            {/* Animated progress bar */}
            <div className="w-72 h-2 bg-gray-200 rounded-full overflow-hidden shadow-inner">
              <div className="h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full loading-progress"></div>
            </div>
          </div>
          
          <style>{`
            @keyframes loadingText {
              0%, 100% { 
                opacity: 0.4; 
                transform: translateY(4px);
              }
              50% { 
                opacity: 1; 
                transform: translateY(0);
              }
            }
            .loading-text-word {
              animation: loadingText 1.8s ease-in-out infinite;
              display: inline-block;
            }
            
            @keyframes loadingDot {
              0%, 100% { 
                opacity: 0.3; 
                transform: scale(0.8);
              }
              50% { 
                opacity: 1; 
                transform: scale(1.2);
              }
            }
            .loading-dot {
              animation: loadingDot 1.2s ease-in-out infinite;
            }
            
            @keyframes loadingProgress {
              0% { 
                width: 0%; 
                opacity: 0.6;
              }
              50% { 
                width: 75%; 
                opacity: 1;
              }
              100% { 
                width: 100%; 
                opacity: 0.8;
              }
            }
            .loading-progress {
              animation: loadingProgress 2.5s ease-in-out infinite;
            }
            
            @keyframes loadingBrand {
              0%, 100% { 
                opacity: 0.7; 
                transform: scale(1);
              }
              50% { 
                opacity: 1; 
                transform: scale(1.02);
              }
            }
            .loading-brand {
              animation: loadingBrand 2s ease-in-out infinite;
            }
          `}</style>
        </div>
      </div>
    );
  }

  const currentPerms: string[] = formData.permissions ?? [];
  const isFullAccessByPerms = hasFullPermissions(currentPerms);
  
  // Determine current manager type based on permissions
  // Basic manager permissions - view tasks, view sessions, and view_client_details (which requires view_clients)
  const basicManagerPermissionsRaw = ['view_tasks', 'view_sessions', 'view_client_details'];
  
  // Normalize basic permissions to include dependencies (view_client_details -> view_clients)
  // Helper function to normalize permissions by adding dependencies
  const normalizePermissionsForCheck = (permissions: string[]): string[] => {
    const set = new Set(permissions);
    let changed = true;
    
    // Add all dependencies recursively
    while (changed) {
      changed = false;
      for (const perm of Array.from(set)) {
        const deps = PERMISSION_DEPENDENCIES[perm] || [];
        for (const dep of deps) {
          if (!set.has(dep)) {
            set.add(dep);
            changed = true;
          }
        }
      }
    }
    
    return Array.from(set).sort();
  };
  
  const basicManagerPermissions = normalizePermissionsForCheck(basicManagerPermissionsRaw);
  const normalizedCurrentPerms = normalizePermissionsForCheck(currentPerms);
  
  // Check if current permissions match exactly the normalized basic permissions
  const isRegularManagerByPerms = !isFullAccessByPerms && 
    normalizedCurrentPerms.length === basicManagerPermissions.length &&
    basicManagerPermissions.every(p => normalizedCurrentPerms.includes(p)) &&
    normalizedCurrentPerms.every(p => basicManagerPermissions.includes(p));
  const isFreeChoiceByPerms = !isFullAccessByPerms && !isRegularManagerByPerms;

  // Use override if set, otherwise use calculated values
  const isFullAccess = managerTypeOverride === 'full-access' || (managerTypeOverride === null && isFullAccessByPerms);
  const isRegularManager = managerTypeOverride === 'regular' || (managerTypeOverride === null && isRegularManagerByPerms);
  const isFreeChoice = managerTypeOverride === 'free-choice' || (managerTypeOverride === null && isFreeChoiceByPerms);

  const handleFullAccessToggle = async (enabled: boolean) => {
    if (!company || !id || !formData) return;
    
    if (enabled) {
      // Show confirmation dialog
      setConfirmDialog({
        isOpen: true,
        title: t('pages.managerProfile.confirmManagerTypeTitle'),
        message: t('pages.managerProfile.confirmFullAccessMessage'),
        onConfirm: async () => {
          setConfirmDialog({ ...confirmDialog, isOpen: false });
          
          // Set all permissions and save immediately
          const allPermissions = [...ALL_PERMISSIONS];
          try {
            await updateManager(company, id, {
              ...formData,
              permissions: allPermissions,
            });
            
            setFormData({ ...formData, permissions: allPermissions });
            setManagerTypeOverride('full-access');
            toast.success(t('pages.managerProfile.setFullAccessSuccess'));
          } catch (err: unknown) {
            console.error(err);
            const errorMessage = (err as any)?.message || t('pages.managerProfile.permissionsUpdateError');
            toast.error(errorMessage);
          }
        },
      });
    } else {
      // Keep current permissions (don't clear, just stop forcing all)
      // User can manually edit from here
      setFormData({ ...formData, permissions: formData.permissions || [] });
    }
  };

  // Set manager to regular manager with basic permissions only
  const handleSetRegularManager = async () => {
    if (!company || !id || !formData) return;
    
    // Show confirmation dialog
    setConfirmDialog({
      isOpen: true,
      title: t('pages.managerProfile.confirmManagerTypeTitle'),
      message: t('pages.managerProfile.confirmRegularMessage'),
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        
        // Basic manager permissions - view tasks, view sessions (for login sessions tab in settings), and view_client_details (for viewing own clients' profiles)
        // Note: view_client_details requires view_clients as dependency, so we need to include it
        const basicManagerPermissionsRaw = [
          'view_tasks',
          'view_sessions',
          'view_client_details',
        ];
        
        // Normalize to include dependencies (view_client_details -> view_clients)
        const basicManagerPermissions = normalizePermissionsForCheck(basicManagerPermissionsRaw);
        
        try {
          await updateManager(company, id, {
            ...formData,
            permissions: basicManagerPermissions,
          });
          
          setFormData({ ...formData, permissions: basicManagerPermissions });
          setManagerTypeOverride('regular');
          toast.success(t('pages.managerProfile.setRegularSuccess'));
        } catch (err: unknown) {
          console.error(err);
          const errorMessage = (err as any)?.message || t('pages.managerProfile.permissionsUpdateError');
          toast.error(errorMessage);
        }
      },
    });
  };

  // Handle free choice mode - allow manual permission selection
  const handleFreeChoice = () => {
    if (!company || !id || !formData) return;
    
    // Switch to free choice mode - keep current permissions but allow manual editing
    // Set override immediately for UI update
    setManagerTypeOverride('free-choice');
    
    // If currently in full access mode (by permissions), we can just switch to free choice
    // No need to save to backend - user can manually edit permissions from here
    // The UI will update immediately thanks to the override state
  };

  const handlePermissionToggle = (permission: string, enabled: boolean) => {
    const updated = applyDependenciesOnToggle(currentPerms, permission, enabled);
    setFormData({ ...formData, permissions: updated });
  };

  return (
    <div className="p-4 sm:p-6 md:p-8" dir="rtl">
      <div className="mx-auto w-full max-w-3xl bg-white shadow-xl border border-blue-100 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-l from-blue-50 to-white border-b border-blue-100">
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => navigate(`/${companyParam || company}/managers`)}
              className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-white border border-blue-200 hover:bg-blue-50 hover:border-blue-300 text-blue-700 transition-colors duration-200 shadow-sm flex-shrink-0"
              title={t('pages.managerProfile.backToUsersList')}
            >
              <FiArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <div className="flex-1 min-w-0 text-center">
              <h2 className="text-lg sm:text-xl md:text-2xl font-extrabold text-blue-900 truncate px-2" title={t('pages.managerProfile.editUserDetails')}>{t('pages.managerProfile.editUserDetails')}</h2>
              <p className="text-xs sm:text-sm text-blue-900/60 mt-1 hidden sm:block">{t('pages.managerProfile.subtitle')}</p>
            </div>
            <div className="w-9 h-9 sm:w-10 sm:h-10 flex-shrink-0" /> {/* Spacer to balance the back button */}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Basic info */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-semibold mb-1 text-gray-700">
                {t('pages.managerProfile.fullName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                readOnly={isReadOnly}
                disabled={isReadOnly}
                maxLength={50}
                title={isReadOnly ? t('pages.managerProfile.readOnlyHint') : undefined}
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                  isReadOnly
                    ? 'bg-gray-100 cursor-not-allowed opacity-75'
                    : validationErrors.fullName
                    ? 'border-red-400 focus:ring-red-300'
                    : !validationErrors.fullName && formData.fullName.trim().length >= 2 && focusedFields.fullName
                    ? 'border-green-300 focus:ring-green-300'
                    : 'border-gray-300 focus:ring-blue-300'
                }`}
                value={formData.fullName}
                onFocus={() => !isReadOnly && setFocusedFields({ ...focusedFields, fullName: true })}
                onBlur={() => !isReadOnly && setFocusedFields({ ...focusedFields, fullName: false })}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (isReadOnly) return;
                  setFormData({ ...formData, fullName: e.target.value });
                  // Validate while typing
                  setValidationErrors({
                    ...validationErrors,
                    fullName: validateField('fullName', e.target.value),
                  });
                }}
                placeholder={t('pages.managerProfile.fullNamePlaceholder')}
              />
              {validationErrors.fullName && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span>⚠️</span> {validationErrors.fullName}
                </p>
              )}
              {!validationErrors.fullName && formData.fullName.trim().length >= 2 && focusedFields.fullName && (
                <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                  <span>✓</span> {t('pages.managerProfile.valid')}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {t('pages.managerProfile.fullNameHint')}
              </p>
            </div>
            <div>
              <label className="block font-semibold mb-1 text-gray-700">
                {t('pages.managerProfile.email')} <span className="text-red-500">*</span>
              </label>
              <EmailInput
                value={formData.email}
                disabled={isReadOnly}
                title={isReadOnly ? t('pages.managerProfile.readOnlyHint') : undefined}
                onChange={(value) => {
                  if (isReadOnly) return;
                  setFormData({ ...formData, email: value });
                  // Clear error when user types
                  if (validationErrors.email) {
                    setValidationErrors({ ...validationErrors, email: '' });
                  }
                }}
                onValidityChange={(isValid) => {
                  if (isReadOnly) return;
                  if (!isValid && formData.email.trim()) {
                    // Error will be shown by EmailInput component
                  } else {
                    setValidationErrors({ ...validationErrors, email: '' });
                  }
                }}
                onFocus={() => !isReadOnly && setFocusedFields({ ...focusedFields, email: true })}
                onBlur={() => !isReadOnly && setFocusedFields({ ...focusedFields, email: false })}
                error={validationErrors.email}
                placeholder={t('pages.managerProfile.emailPlaceholder')}
                className={`rounded-lg ${isReadOnly ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`}
                required={true}
                validateOnBlur={true}
              />
              {validationErrors.email && (
                <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                  <span>⚠️</span>
                  {validationErrors.email}
                </p>
              )}
              {!validationErrors.email && formData.email.trim() && /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email.trim()) && focusedFields.email && (
                <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                  <span>✓</span> {t('pages.managerProfile.valid')}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {t('pages.managerProfile.emailHint')}
              </p>
            </div>
            <div>
              <label className="block font-semibold mb-1 text-gray-700">{t('pages.managerProfile.phone')}</label>
              <PhoneInput
                value={formData.phone || ''}
                disabled={isReadOnly}
                title={isReadOnly ? t('pages.managerProfile.readOnlyHint') : undefined}
                onChange={(value) => {
                  if (isReadOnly) return;
                  setFormData({ ...formData, phone: value });
                  // Validate while typing
                  setValidationErrors({
                    ...validationErrors,
                    phone: validateField('phone', value),
                  });
                }}
                onFocus={() => !isReadOnly && setFocusedFields({ ...focusedFields, phone: true })}
                onBlur={() => !isReadOnly && setFocusedFields({ ...focusedFields, phone: false })}
                placeholder={t('pages.managerProfile.phonePlaceholder')}
                className={`${
                  isReadOnly
                    ? 'bg-gray-100 cursor-not-allowed opacity-75'
                    : validationErrors.phone
                    ? 'border-red-400'
                    : !validationErrors.phone && formData.phone && formData.phone.trim() && focusedFields.phone
                    ? 'border-green-300'
                    : 'border-gray-300'
                }`}
                error={validationErrors.phone || undefined}
                validateOnBlur={false}
              />
              {!validationErrors.phone && formData.phone && formData.phone.trim() && focusedFields.phone && (
                <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                  <span>✓</span> {t('pages.managerProfile.valid')}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {t('pages.managerProfile.phoneHint')}
              </p>
            </div>
          </section>

          <Separator className="my-2" />

          {/* Permission groups */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-blue-900 text-lg">{t('pages.managerProfile.permissionsTitle')}</h3>
            </div>
            
            {/* Full Access / Regular Manager Switch */}
            <div className="mb-4 space-y-3">
              {/* Full Access Toggle */}
              <div className={`p-4 rounded-xl border-2 transition-all ${
                isFullAccess 
                  ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-400 shadow-md' 
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="managerType"
                    checked={isFullAccess}
                    disabled={isReadOnly}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      if (e.target.checked) {
                        setManagerTypeOverride('full-access');
                        handleFullAccessToggle(true);
                      }
                    }}
                    className={`w-5 h-5 accent-blue-600 ${isReadOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  />
                  <div className="flex-1">
                    <div className="font-bold text-blue-900">{t('pages.managerProfile.fullAccess')}</div>
                    <div className="text-sm text-blue-700">
                      {isFullAccess 
                        ? t('pages.managerProfile.fullAccessDescActive')
                        : t('pages.managerProfile.fullAccessDescInactive')}
                    </div>
                  </div>
                </label>
              </div>
              
              {/* Regular Manager Button */}
              <div className={`p-4 rounded-xl border-2 transition-all ${
                isRegularManager
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-400 shadow-md'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="managerType"
                    checked={isRegularManager}
                    disabled={isReadOnly}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      if (e.target.checked) {
                        setManagerTypeOverride('regular');
                        handleSetRegularManager();
                      }
                    }}
                    className={`w-5 h-5 accent-green-600 ${isReadOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  />
                  <div className="flex-1">
                    <div className="font-bold text-blue-900">{t('pages.managerProfile.regularManager')}</div>
                    <div className="text-sm text-blue-700">
                      {t('pages.managerProfile.regularManagerDesc')}
                    </div>
                  </div>
                </label>
              </div>
              
              {/* Free Choice Button */}
              <div className={`p-4 rounded-xl border-2 transition-all ${
                isFreeChoice
                  ? 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-400 shadow-md'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="managerType"
                    checked={isFreeChoice}
                    disabled={isReadOnly}
                    onChange={(e) => {
                      if (isReadOnly) return;
                      if (e.target.checked) {
                        setManagerTypeOverride('free-choice');
                        handleFreeChoice();
                      }
                    }}
                    className={`w-5 h-5 accent-purple-600 ${isReadOnly ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  />
                  <div className="flex-1">
                    <div className="font-bold text-blue-900">{t('pages.managerProfile.freeChoice')}</div>
                    <div className="text-sm text-blue-700">
                      {t('pages.managerProfile.freeChoiceDesc')}
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="space-y-3">
              {permissionGroups.map((group) => {
                const isOpen = openSections.includes(group.key);
                const allSelected = group.permissions.every((p) => currentPerms.includes(p));

                return (
                  <div key={group.key} className="border rounded-xl shadow-sm">
                    {/* group header */}
                    <button
                      type="button"
                      onClick={() => toggleSection(group.key)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 transition"
                    >
                      <div className="flex items-center gap-2 text-blue-900 font-bold">
                        <span className="text-lg">{group.icon}</span>
                        <span>{t(`pages.managerProfile.groupTitle_${group.key}`)}</span>
                      </div>
                      <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>⌄</span>
                    </button>

                    {/* group body */}
                    <div
                      className={`px-4 overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1200px] py-3' : 'max-h-0'
                        }`}
                    >
                      <div className="flex items-center justify-end mb-2">
                        <button
                          type="button"
                          disabled={isFullAccess || isReadOnly}
                          className={`text-blue-600 text-sm underline hover:text-blue-800 ${isFullAccess || isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                          onClick={() => {
                            if (isFullAccess || isReadOnly) return;
                            const next = allSelected
                              ? currentPerms.filter((p) => !group.permissions.includes(p))
                              : Array.from(new Set<string>([...currentPerms, ...group.permissions]));
                            setFormData({ ...formData, permissions: next });
                          }}
                        >
                          {allSelected ? t('pages.managerProfile.deselectAll') : t('pages.managerProfile.selectAll')}
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {group.permissions.map((permission) => {
                          const checked = currentPerms.includes(permission);
                          const permType = PERMISSION_TYPES[permission];
                          const permTypeLabel = permType ? PERMISSION_TYPE_LABELS[permType] : '';
                          const permDescription = PERMISSION_DESCRIPTIONS[permission] || '';
                          // Tooltip text - use description if available, otherwise use label
                          // Ensure proper RTL formatting for Hebrew text only
                          const permKey = `pages.managerProfile.permission_${permission}`;
                          const translatedPerm = t(permKey);
                          const permLabel = (translatedPerm && translatedPerm !== permKey) ? translatedPerm : (permissionLabels[permission] || permission);
                          const tooltipText = permDescription 
                            ? (permTypeLabel ? `${permDescription} (${permTypeLabel})` : permDescription)
                            : permLabel;
                          
                          return (
                            <label
                              key={permission}
                              className="flex items-center gap-2 p-2 border rounded-lg bg-white"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={isFullAccess || isReadOnly}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                  if (isReadOnly) return;
                                  handlePermissionToggle(permission, e.target.checked);
                                }}
                              />
                              <Tooltip text={tooltipText} side="right" maxWidth={300}>
                                <span className="text-sm flex items-center gap-1 cursor-help">
                                  <span className="font-medium">
                                    {permLabel}
                                  </span>
                                  {permTypeLabel && (
                                    <span className="text-xs text-gray-500 font-normal">
                                      ({permTypeLabel})
                                    </span>
                                  )}
                                </span>
                              </Tooltip>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={() => setIsPasswordModalOpen(true)}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700"
            >
              {t('pages.managerProfile.changePassword')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || isReadOnly}
              className="px-6 py-2 rounded-lg bg-green-600 text-white font-bold hover:bg-green-700 disabled:opacity-60"
              title={isReadOnly ? t('pages.managerProfile.cannotEditReadOnly') : ''}
            >
              {saving ? t('pages.managerProfile.saving') : t('pages.managerProfile.saveChanges')}
            </button>
          </div>
        </div>
      </div>

      {/* Password Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-lg w-96 max-w-[92vw] p-6 relative">
            <h2 className="text-xl font-bold mb-4 text-blue-800 text-center">{t('pages.managerProfile.changePasswordTitle')}</h2>

            <div className="relative mb-4">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('pages.managerProfile.newPasswordPlaceholder')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring focus:ring-blue-200 pr-4"
                value={newPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label={t('pages.managerProfile.showHidePassword')}
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsPasswordModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                {t('pages.managerProfile.cancel')}
              </button>
              <button
                type="button"
                disabled={isSavingPassword}
                onClick={handlePasswordSave}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
              >
                {isSavingPassword ? t('pages.managerProfile.updatingPassword') : t('pages.managerProfile.savePassword')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" dir="rtl">
          <div className="bg-white rounded-xl shadow-lg w-96 max-w-[92vw] p-6 relative">
            <h2 className="text-xl font-bold mb-4 text-blue-800 text-center">{confirmDialog.title}</h2>
            
            <p className="text-gray-700 mb-6 text-center">{confirmDialog.message}</p>

            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold transition-colors"
              >
                {t('pages.managerProfile.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmDialog.onConfirm}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors"
              >
                {t('pages.managerProfile.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerProfile;
